import { CircularProgress, InputAdornment, Typography, MenuItem, Select } from "@mui/material";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import {
  setCurrentStage,
  STAGES,
  useContactChangeState,
} from "src/store/features/contactChange.slice";
import OnfidoForm from "src/components/settings/onfido/OnfidoVerification";
import ContainedButton from "src/components/common/ContainedButton";
import useContactChange, { MFA_INPUT_STEPS } from "./useContactChange";
import { useDispatch } from "react-redux";
import { ExclamationMarkSolidNavyIcon } from "src/icons/ExclamationMarkSolidNavyIcon";
import { InfoSolidNavyIcon } from "src/icons/InfoSolidNavyIcon";
import { ExclamationMarkOutlineRedIcon } from "src/icons/ExclamationMarkOutlineRedIcon";
import { CheckSolidNavyIcon } from "src/icons/CheckSolidNavyIcon";
import { CheckSolidGreenIcon } from "src/icons/CheckSolidGreenIcon";
import { EyeOpenIcon } from "src/icons/EyeOpenIcon";
import { EyeClosedIcon } from "src/icons/EyeClosedIcon";
import { ErrorSolidRedIcon } from "src/icons/ErrorSolidRedIcon";
import { BackOutlineWhiteIcon } from "src/icons/BackOutlineWhiteIcon";
import { CrossOutlineWhiteIcon } from "src/icons/CrossOutlineWhiteIcon";
import { CountryIso2, defaultCountries, FlagEmoji, parseCountry } from "react-international-phone";
import { useUserSlice } from "src/store/features/user.slice";
import {
  BannerContentWrapper,
  BannerDetails,
  BannerDetailsText,
  ButtonText,
  ContentHeadingTitleMobile,
  ContentHeadingTypography,
  ContentHeadingWrapperMobile,
  ContentWrapper,
  ContentWrapperMobile,
  DialogWrapper,
  FormInputErrorLinkText,
  FormInputErrorText,
  FormInputErrorWrapper,
  FormInputFieldBox,
  FormInputFieldText,
  FormInputFieldWrapper,
  FormInputHeading,
  FormInputSubHeading,
  FormInputWrapper,
  HeaderIcon,
  HeaderIconMobile,
  HeaderTitle,
  HeaderTitleMobile,
  HeaderWrapper,
  HeaderWrapperMobile,
  LimitationsContentWrapper,
  LimitationsLineTypography,
  LimitationsLineWrapper,
  PasswordInputFieldIcon,
  PasswordInputHeadingWrapper,
  InputSubmitWrapper,
  PushButtonToBottom,
  QuitButton,
  QuitButtonsWrapper,
  QuitTextLineWrapper,
  QuitTextSubtitle,
  QuitTextTitle,
  QuitTextWrapper,
  SubContentWrapperMobile,
} from "./styles/styledComponents";

export default function ContactChangeModal() {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  const { is2FAEnabled } = useUserSlice();

  const {
    isContactChangeModalOpen,
    currentStage,
    previousStage,
    contactChangeType,
    changingValueName,
    secondaryAuthValueName,
    userEmail,
    userPhone,
    secondaryAuthValue,
    requestedValue,
  } = useContactChangeState();

  const {
    handleClose,
    title,
    handleNextButtonClick,
    currentStep,
    passwordError,
    password,
    handlePasswordChange,
    passwordLoading,
    isPasswordVisible,
    togglePasswordVisibility,
    secondaryAuthValueCodeError,
    secondaryAuthValueCode,
    handleSecondaryAuthValueCodeChange,
    secondaryAuthValueCodeLoading,
    newValueError,
    newValue,
    handleNewValueChange,
    newValueLoading,
    newValueCodeError,
    newValueCode,
    handleNewValueCodeChange,
    newValueCodeLoading,
    twoFACodeError,
    twoFACode,
    handleTwoFACodeChange,
    twoFACodeLoading,
    confirmButtonRef,
    handleAcknowledgeClick,
    handleQuitFormButton,
    acknowledgeLoading,
    submitLoading,
    clearMfaLoading,
    submitErrorMessage,
    handleResendSecondaryAuthCode,
    handleResendRequestedAuthCode,
    resendSecondaryAuthCodeLoading,
    resendRequestedAuthCodeLoading,
    isSameContactValue,
    passwordInputRef,
    secondaryAuthValueCodeInputRef,
    newValueInputRef,
    newValueCodeInputRef,
    twoFACodeInputRef,
    isExpired,
    phoneCountry,
    handlePhoneCountryChange,
    handleSubmitPassword,
    handleNewValueSubmit,
  } = useContactChange();

  const ModalHeader = () => {
    return !isMobile ? (
      <HeaderWrapper>
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderIcon onClick={handleClose}>
          <CrossOutlineWhiteIcon />
        </HeaderIcon>
      </HeaderWrapper>
    ) : (
      <HeaderWrapperMobile>
        <HeaderIconMobile onClick={handleClose}>
          <BackOutlineWhiteIcon />
        </HeaderIconMobile>
        <HeaderTitleMobile>
          <CalebAndBrownLogo
            height={40}
            width={141}
            color="currentColor"
            aria-label="Caleb & Brown"
          />
        </HeaderTitleMobile>
        {/* Empty Icon to push title to centre */}
        <HeaderIconMobile />
      </HeaderWrapperMobile>
    );
  };

  const LimitationsContent = () => {
    return (
      <LimitationsContentWrapper>
        {currentStage === STAGES.COMPLETED && (
          <LimitationsLineWrapper>
            <LimitationsLineTypography>
              There will be limitations to your account for{" "}
              <span style={{ fontWeight: 500 }}>
                24 hours once your contact change is approved:
              </span>
            </LimitationsLineTypography>
          </LimitationsLineWrapper>
        )}
        <LimitationsLineWrapper>
          <CheckSolidGreenIcon />
          <LimitationsLineTypography>
            You can still place a trade, target or schedule an order
          </LimitationsLineTypography>
        </LimitationsLineWrapper>
        <LimitationsLineWrapper>
          <CheckSolidGreenIcon />
          <LimitationsLineTypography>You can still deposit cash or coins</LimitationsLineTypography>
        </LimitationsLineWrapper>
        <LimitationsLineWrapper>
          <ErrorSolidRedIcon />
          <LimitationsLineTypography>
            You cannot withdraw assets for 24 hours
          </LimitationsLineTypography>
        </LimitationsLineWrapper>
      </LimitationsContentWrapper>
    );
  };

  const AcknowledgeStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        <ContentHeadingTypography>
          Please note: as an additional layer of security, there will be limitations on your account
          for 24 hours once your contact change is approved. This means:
        </ContentHeadingTypography>
        <LimitationsContent />
        <ContainedButton
          height="46px"
          color="salmon"
          disabled={acknowledgeLoading}
          onClick={handleAcknowledgeClick}
          endIcon={
            <InputAdornment position="end" style={{ width: 20 }}>
              {acknowledgeLoading && <CircularProgress size={20} style={{ color: "white" }} />}
            </InputAdornment>
          }>
          <ButtonText>I acknowledge</ButtonText>
        </ContainedButton>
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
          <ContentHeadingTypography>
            Please note: as an additional layer of security, there will be limitations on your
            account for 24 hours once your contact change is approved. This means:
          </ContentHeadingTypography>
        </ContentHeadingWrapperMobile>
        <SubContentWrapperMobile>
          <LimitationsContent />
          <PushButtonToBottom />
          <ContainedButton
            height="46px"
            color="salmon"
            disabled={acknowledgeLoading}
            onClick={handleAcknowledgeClick}
            endIcon={
              <InputAdornment position="end">
                {acknowledgeLoading && <CircularProgress />}
              </InputAdornment>
            }>
            <ButtonText>I acknowledge</ButtonText>
          </ContainedButton>
        </SubContentWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const FormPasswordInput = () => {
    const PasswordInputEndAdornment = () => {
      if (passwordLoading) {
        return <CircularProgress size={12} />;
      } else if (currentStep > MFA_INPUT_STEPS.PASSWORD) {
        return <CheckSolidGreenIcon />;
      } else {
        return <InputSubmitWrapper onClick={handleSubmitPassword}>Submit</InputSubmitWrapper>;
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        handleSubmitPassword();
      }
    };

    return (
      <FormInputWrapper isActive={currentStep >= MFA_INPUT_STEPS.PASSWORD}>
        <PasswordInputHeadingWrapper>
          <FormInputHeading includeFlex={false}>Step 1: Enter Portal password</FormInputHeading>
          <PasswordInputFieldIcon hasOnClick={true} onClick={togglePasswordVisibility}>
            {isPasswordVisible ? (
              <EyeOpenIcon color="#FC4C4C" />
            ) : (
              <EyeClosedIcon color="#FC4C4C" />
            )}
          </PasswordInputFieldIcon>
        </PasswordInputHeadingWrapper>
        <FormInputSubHeading>
          Please enter your password for Caleb & Brown Portal login
        </FormInputSubHeading>
        <FormInputFieldWrapper>
          <FormInputFieldBox
            hasError={passwordError}
            isActive={currentStep === MFA_INPUT_STEPS.PASSWORD}>
            <FormInputFieldText
              id="contact-change-password"
              autoFocus={currentStep === MFA_INPUT_STEPS.PASSWORD}
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter password here"
              disabled={currentStep !== MFA_INPUT_STEPS.PASSWORD || passwordLoading}
              type={isPasswordVisible ? "text" : "password"}
              ref={passwordInputRef}
            />
            <PasswordInputEndAdornment />
          </FormInputFieldBox>
        </FormInputFieldWrapper>
        {passwordError && (
          <FormInputErrorWrapper>
            <ExclamationMarkOutlineRedIcon />
            <FormInputErrorText>Incorrect password, please try again.</FormInputErrorText>
          </FormInputErrorWrapper>
        )}
      </FormInputWrapper>
    );
  };

  const FormSecondaryAuthInput = () => {
    let secondaryAuthSubheading = `We sent a verification code to your registered ${secondaryAuthValueName} `;
    if (secondaryAuthValue) {
      const secondaryAuthValueText =
        contactChangeType === "email"
          ? `ending in ${secondaryAuthValue?.slice(-4)}`
          : secondaryAuthValue;
      secondaryAuthSubheading += secondaryAuthValueText;
    }

    const SecondaryAuthInputEndAdornment = () => {
      if (secondaryAuthValueCodeLoading) {
        return <CircularProgress size={12} />;
      } else if (currentStep > MFA_INPUT_STEPS.SECONDARY_AUTH) {
        return <CheckSolidGreenIcon />;
      } else {
        return null;
      }
    };

    return (
      <FormInputWrapper isActive={currentStep >= MFA_INPUT_STEPS.SECONDARY_AUTH}>
        <FormInputHeading>Step 2: Verify {secondaryAuthValueName}</FormInputHeading>
        <FormInputSubHeading>{secondaryAuthSubheading}</FormInputSubHeading>
        <FormInputFieldWrapper>
          <FormInputFieldBox
            hasError={secondaryAuthValueCodeError}
            isActive={currentStep === MFA_INPUT_STEPS.SECONDARY_AUTH}>
            <FormInputFieldText
              id="contact-change-secondary-auth-value-code"
              autoFocus={currentStep === MFA_INPUT_STEPS.SECONDARY_AUTH}
              value={secondaryAuthValueCode}
              onChange={handleSecondaryAuthValueCodeChange}
              placeholder="Enter code here"
              disabled={
                currentStep !== MFA_INPUT_STEPS.SECONDARY_AUTH || secondaryAuthValueCodeLoading
              }
              ref={secondaryAuthValueCodeInputRef}
            />
            <SecondaryAuthInputEndAdornment />
          </FormInputFieldBox>
          {secondaryAuthValueCodeError && (
            <FormInputErrorWrapper>
              <ExclamationMarkOutlineRedIcon />
              <FormInputErrorText>
                Incorrect code, please try again. Click{" "}
                <FormInputErrorLinkText
                  onClick={handleResendSecondaryAuthCode}
                  disabled={resendSecondaryAuthCodeLoading}>
                  {` here`}
                </FormInputErrorLinkText>{" "}
                to resend code.
              </FormInputErrorText>
            </FormInputErrorWrapper>
          )}
        </FormInputFieldWrapper>
      </FormInputWrapper>
    );
  };

  const FormNewValueInput = () => {
    const PhoneCountryInputStartAdornment = () => {
      return (
        <InputAdornment position="start" style={{ border: "none", marginTop: "0px" }}>
          <Select
            MenuProps={{
              sx: {
                "& .MuiPaper-root": {
                  width: "100%",
                  maxWidth: "600px",
                  marginLeft: { xs: "10px", sm: "0px" },
                },
              },
              style: {
                height: "300px",
                top: "10px",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: 30,
              },
            }}
            disableUnderline
            variant="standard"
            value={phoneCountry}
            onChange={(e) => handlePhoneCountryChange(e.target.value as CountryIso2)}
            renderValue={(value) => (
              <FlagEmoji iso2={value} style={{ display: "flex", width: "20px" }} />
            )}>
            {defaultCountries.map((c) => {
              const country = parseCountry(c);
              return (
                <MenuItem key={country.iso2} value={country.iso2}>
                  <FlagEmoji iso2={country.iso2} style={{ marginRight: "8px", width: "20px" }} />
                  <Typography marginRight="8px">{country.name}</Typography>
                  <Typography color="gray">+{country.dialCode}</Typography>
                </MenuItem>
              );
            })}
          </Select>
        </InputAdornment>
      );
    };

    const NewValueInputEndAdornment = () => {
      if (newValueLoading) {
        return <CircularProgress size={12} />;
      } else if (currentStep > MFA_INPUT_STEPS.NEW_VALUE) {
        return <CheckSolidGreenIcon />;
      } else {
        return <InputSubmitWrapper onClick={handleNewValueSubmit}>Submit</InputSubmitWrapper>;
      }
    };

    let errorText = `Field required, please enter your new ${changingValueName} to proceed.`;
    if (isSameContactValue) {
      errorText = "Please enter a different value to your current contact details.";
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        handleNewValueSubmit();
      }
    };

    return (
      <FormInputWrapper isActive={currentStep >= MFA_INPUT_STEPS.NEW_VALUE}>
        <FormInputHeading>Step 3: Enter new {changingValueName}</FormInputHeading>
        <FormInputSubHeading>Please enter your new {changingValueName}</FormInputSubHeading>
        <FormInputFieldWrapper>
          <FormInputFieldBox
            hasError={newValueError}
            isActive={currentStep === MFA_INPUT_STEPS.NEW_VALUE}>
            {contactChangeType === "phone" && <PhoneCountryInputStartAdornment />}
            <FormInputFieldText
              id="contact-change-new-value"
              autoFocus={currentStep === MFA_INPUT_STEPS.NEW_VALUE}
              value={newValue}
              onChange={handleNewValueChange}
              onKeyDown={handleKeyDown}
              placeholder={`Enter new ${changingValueName} here`}
              disabled={currentStep !== MFA_INPUT_STEPS.NEW_VALUE || newValueLoading}
              ref={newValueInputRef}
            />
            <NewValueInputEndAdornment />
          </FormInputFieldBox>
          {newValueError && (
            <FormInputErrorWrapper>
              <ExclamationMarkOutlineRedIcon />
              <FormInputErrorText>{errorText}</FormInputErrorText>
            </FormInputErrorWrapper>
          )}
        </FormInputFieldWrapper>
      </FormInputWrapper>
    );
  };

  const FormNewValueCodeInput = () => {
    const NewValueCodeInputEndAdornment = () => {
      if (newValueCodeLoading) {
        return <CircularProgress size={12} />;
      } else if (currentStep > MFA_INPUT_STEPS.NEW_VALUE_CODE) {
        return <CheckSolidGreenIcon />;
      } else {
        return null;
      }
    };

    return (
      <FormInputWrapper isActive={currentStep >= MFA_INPUT_STEPS.NEW_VALUE_CODE}>
        <FormInputHeading>Step 4: Verify new {changingValueName}</FormInputHeading>
        <FormInputSubHeading>
          We sent a verification code to your new {changingValueName}{" "}
          {currentStep >= MFA_INPUT_STEPS.NEW_VALUE_CODE ? requestedValue : ""}
        </FormInputSubHeading>
        <FormInputFieldWrapper>
          <FormInputFieldBox
            hasError={newValueCodeError}
            isActive={currentStep === MFA_INPUT_STEPS.NEW_VALUE_CODE}>
            <FormInputFieldText
              id="contact-change-new-value-code"
              autoFocus={currentStep === MFA_INPUT_STEPS.NEW_VALUE_CODE}
              value={newValueCode}
              onChange={handleNewValueCodeChange}
              placeholder="Enter code here"
              disabled={currentStep !== MFA_INPUT_STEPS.NEW_VALUE_CODE || newValueCodeLoading}
              ref={newValueCodeInputRef}
            />
            <NewValueCodeInputEndAdornment />
          </FormInputFieldBox>
          {newValueCodeError && (
            <FormInputErrorWrapper>
              <ExclamationMarkOutlineRedIcon />
              <FormInputErrorText>
                Incorrect code, please try again. Click{" "}
                <FormInputErrorLinkText
                  onClick={handleResendRequestedAuthCode}
                  disabled={resendRequestedAuthCodeLoading}>
                  {` here`}
                </FormInputErrorLinkText>{" "}
                to resend code.
              </FormInputErrorText>
            </FormInputErrorWrapper>
          )}
        </FormInputFieldWrapper>
      </FormInputWrapper>
    );
  };

  const FormTwoFACodeInput = () => {
    const TwoFACodeInputEndAdornment = () => {
      if (twoFACodeLoading) {
        return <CircularProgress size={12} />;
      } else if (currentStep > MFA_INPUT_STEPS.TWO_FA) {
        return <CheckSolidGreenIcon />;
      } else {
        return null;
      }
    };

    return (
      <FormInputWrapper isActive={currentStep >= MFA_INPUT_STEPS.TWO_FA}>
        <FormInputHeading>Step 5: Complete 2 factor authentication (2FA)</FormInputHeading>
        <FormInputSubHeading>
          Please enter your 2FA code from your authenticator app
        </FormInputSubHeading>
        <FormInputFieldWrapper>
          <FormInputFieldBox
            hasError={twoFACodeError}
            isActive={currentStep === MFA_INPUT_STEPS.TWO_FA}>
            <FormInputFieldText
              id="contact-change-2fa-code"
              autoFocus={currentStep === MFA_INPUT_STEPS.TWO_FA}
              value={twoFACode}
              onChange={handleTwoFACodeChange}
              placeholder="Enter code here"
              disabled={currentStep !== MFA_INPUT_STEPS.TWO_FA || twoFACodeLoading}
              ref={twoFACodeInputRef}
            />
            <TwoFACodeInputEndAdornment />
          </FormInputFieldBox>
          {twoFACodeError && (
            <FormInputErrorWrapper>
              <ExclamationMarkOutlineRedIcon />
              <FormInputErrorText>Incorrect code, please try again.</FormInputErrorText>
            </FormInputErrorWrapper>
          )}
        </FormInputFieldWrapper>
      </FormInputWrapper>
    );
  };

  const FormContent = () => {
    return (
      <>
        <FormPasswordInput />
        <FormSecondaryAuthInput />
        <FormNewValueInput />
        <FormNewValueCodeInput />
        {is2FAEnabled && <FormTwoFACodeInput />}
      </>
    );
  };

  const FormHeader = () => {
    return (
      <>
        {isExpired && (
          <BannerContentWrapper background="#FEFCF8">
            <BannerDetails>
              <InfoSolidNavyIcon />
              <BannerDetailsText>
                Your contact change request has expired. We have reset your progress from the start.
              </BannerDetailsText>
            </BannerDetails>
          </BannerContentWrapper>
        )}

        <ContentHeadingTypography>
          As an added layer of security, we have a multi-factor verification that you will need to
          complete to proceed with your {contactChangeType} change.
        </ContentHeadingTypography>
      </>
    );
  };

  const FormConfirmButton = () => {
    return (
      <>
        <ContainedButton
          height="46px"
          color="salmon"
          disabled={submitLoading}
          onClick={handleNextButtonClick}
          endIcon={
            <InputAdornment position="end" style={{ width: 20 }}>
              {submitLoading && <CircularProgress size={20} style={{ color: "white" }} />}
            </InputAdornment>
          }>
          <ButtonText ref={confirmButtonRef}>Confirm changes</ButtonText>
        </ContainedButton>
        {submitErrorMessage && (
          <FormInputErrorWrapper>
            <ExclamationMarkOutlineRedIcon />
            <FormInputErrorText>{submitErrorMessage}</FormInputErrorText>
          </FormInputErrorWrapper>
        )}
      </>
    );
  };

  const FormStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        <FormHeader />
        <FormContent />
        {currentStep === MFA_INPUT_STEPS.BUTTON_DISPLAY && <FormConfirmButton />}
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
          <FormHeader />
        </ContentHeadingWrapperMobile>
        <SubContentWrapperMobile>
          <FormContent />
          {currentStep === MFA_INPUT_STEPS.BUTTON_DISPLAY && <FormConfirmButton />}
        </SubContentWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const OnfidoStage = () => {
    return <OnfidoForm reason={"Contact Change"} />;
  };

  const AllStepsCompletedContent = () => {
    return (
      <>
        <BannerContentWrapper>
          <BannerDetails>
            <CheckSolidNavyIcon />
            <BannerDetailsText>
              Your request to change your {changingValueName} from{" "}
              <span style={{ fontWeight: 500 }}>
                {contactChangeType === "email" ? userEmail : userPhone}
              </span>{" "}
              to <span style={{ fontWeight: 500 }}>{requestedValue}</span> was successful.
            </BannerDetailsText>
          </BannerDetails>
        </BannerContentWrapper>
        <LimitationsContent />
        <LimitationsLineTypography style={{ alignSelf: "stretch" }}>
          You will receive a notification to your {contactChangeType === "email" ? "new" : ""} email
          address{" "}
          <span style={{ fontWeight: 600 }}>
            {contactChangeType === "email" ? requestedValue : userEmail}
          </span>{" "}
          once this has been completed.
        </LimitationsLineTypography>
        {isMobile && <PushButtonToBottom />}
        <ContainedButton
          height="46px"
          color="salmon"
          onClick={() => {
            dispatch(handleClose);
          }}>
          <ButtonText>Done</ButtonText>
        </ContainedButton>
      </>
    );
  };

  const AllStepsCompletedStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        <AllStepsCompletedContent />
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
        </ContentHeadingWrapperMobile>
        <SubContentWrapperMobile>
          <AllStepsCompletedContent />
        </SubContentWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const QuitStageContent = () => {
    const resumeText =
      previousStage === STAGES.FORM
        ? `Select "Resume" to go back to your ${contactChangeType} change form.`
        : `You have one remaining step for your ${contactChangeType} change, select "Resume" to finalise your ${contactChangeType} change request. `;

    return (
      <>
        <QuitTextWrapper>
          <QuitTextLineWrapper>
            <QuitTextTitle>Your changes will not be saved if you choose to quit now.</QuitTextTitle>
          </QuitTextLineWrapper>
          <QuitTextLineWrapper>
            <QuitTextSubtitle>{resumeText}</QuitTextSubtitle>
          </QuitTextLineWrapper>
        </QuitTextWrapper>
        {isMobile && <PushButtonToBottom />}
        <QuitButtonsWrapper isMobile={isMobile}>
          <QuitButton disabled={clearMfaLoading} onClick={handleQuitFormButton}>
            Quit
          </QuitButton>
          <ContainedButton
            height="46px"
            color="salmon"
            disabled={clearMfaLoading}
            onClick={() => dispatch(setCurrentStage(previousStage))}>
            <ButtonText>Resume</ButtonText>
          </ContainedButton>
        </QuitButtonsWrapper>
      </>
    );
  };

  const QuitStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        <QuitStageContent />
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
        </ContentHeadingWrapperMobile>
        <SubContentWrapperMobile>
          <QuitStageContent />
        </SubContentWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const CannotProcessStageContent = () => {
    return (
      <BannerContentWrapper background="#FFF6F6">
        <BannerDetails>
          <ExclamationMarkSolidNavyIcon />
          <BannerDetailsText>
            Your contact change request could not be processed. Please contact support, or your
            broker.
          </BannerDetailsText>
        </BannerDetails>
      </BannerContentWrapper>
    );
  };

  const CannotProcessStage = () => {
    return !isMobile ? (
      <ContentWrapper>
        <CannotProcessStageContent />
      </ContentWrapper>
    ) : (
      <ContentWrapperMobile>
        <ContentHeadingWrapperMobile>
          <ContentHeadingTitleMobile>{title}</ContentHeadingTitleMobile>
          <CannotProcessStageContent />
        </ContentHeadingWrapperMobile>
      </ContentWrapperMobile>
    );
  };

  const ModalContent = () => {
    switch (currentStage) {
      case STAGES.CANNOT_PROCESS:
        return <CannotProcessStage />;
      case STAGES.ACKNOWLEDGE:
        return <AcknowledgeStage />;
      case STAGES.FORM:
        return <FormStage />;
      case STAGES.ONFIDO:
        return <OnfidoStage />;
      case STAGES.COMPLETED:
        return <AllStepsCompletedStage />;
      case STAGES.QUIT:
        return <QuitStage />;
      default:
        return null;
    }
  };

  return (
    <DialogWrapper
      open={isContactChangeModalOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={!isMobile ? false : undefined} // disable modal width limit for desktop
    >
      <ModalHeader />
      <ModalContent />
    </DialogWrapper>
  );
}
